import React, { useState, useRef } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import AvatarCropper from "./AvatarCropper";
import { supabase } from "../../lib/supabase";

export default function AvatarUpload({ userId, currentAvatarUrl, onAvatarChange }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);

    e.target.value = "";
  };

  const handleCropDone = async (croppedBlob) => {
    if (!userId) return;
    setUploading(true);

    try {
      const fileName = `${userId}/avatar-${Date.now()}.jpg`;

      const { data: listData } = await supabase.storage
        .from("avatars")
        .list(userId);

      if (listData && listData.length > 0) {
        const filesToRemove = listData.map((f) => `${userId}/${f.name}`);
        await supabase.storage.from("avatars").remove(filesToRemove);
      }

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(fileName, croppedBlob, {
          contentType: "image/jpeg",
          upsert: true
        });

      if (uploadError) {
        console.error("Upload error:", uploadError);
        setUploading(false);
        return;
      }

      const { data: urlData } = supabase.storage
        .from("avatars")
        .getPublicUrl(fileName);

      const publicUrl = urlData?.publicUrl + "?t=" + Date.now();

      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl, updated_at: new Date().toISOString() })
        .eq("id", userId);

      if (!updateError) {
        onAvatarChange(publicUrl);
      }
    } catch (err) {
      console.error("Avatar upload failed:", err);
    } finally {
      setUploading(false);
      setShowCropper(false);
      setImageSrc(null);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!userId) return;
    setRemoving(true);

    try {
      const { data: listData } = await supabase.storage
        .from("avatars")
        .list(userId);

      if (listData && listData.length > 0) {
        const filesToRemove = listData.map((f) => `${userId}/${f.name}`);
        await supabase.storage.from("avatars").remove(filesToRemove);
      }

      await supabase
        .from("profiles")
        .update({ avatar_url: "", updated_at: new Date().toISOString() })
        .eq("id", userId);

      onAvatarChange("");
    } catch (err) {
      console.error("Avatar remove failed:", err);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div id="avatar-upload_section">
      <label className="block text-lg font-extralight text-neutral-400 mb-3">
        Фото профиля
      </label>

      <div className="flex items-center gap-6">
        <div className="relative group">
          {currentAvatarUrl ? (
            <img
              src={currentAvatarUrl}
              alt="Аватар"
              className="w-24 h-24 rounded-full object-cover border-2 border-neutral-700"
            />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center border-2 border-neutral-700">
              <Camera className="w-8 h-8 text-white/70" />
            </div>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity"
          >
            <Camera className="w-6 h-6 text-white" />
          </button>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500/10 text-primary-400 hover:bg-primary-500/20 text-lg font-extralight transition-colors"
          >
            <Camera className="w-4 h-4" />
            {currentAvatarUrl ? "Изменить фото" : "Загрузить фото"}
          </button>

          {currentAvatarUrl && (
            <button
              onClick={handleRemoveAvatar}
              disabled={removing}
              className="flex items-center gap-2 px-4 py-2 rounded-md hover:bg-red-500/10 text-neutral-500 hover:text-red-400 text-lg font-extralight transition-colors disabled:opacity-50"
            >
              {removing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4" />
              )}
              Удалить фото
            </button>
          )}

          <p className="text-lg font-extralight text-neutral-500">
            JPG, PNG или WebP. Макс. 5 МБ
          </p>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileSelect}
        className="hidden"
      />

      {showCropper && imageSrc && (
        <AvatarCropper
          imageSrc={imageSrc}
          uploading={uploading}
          onCropDone={handleCropDone}
          onCancel={() => {
            setShowCropper(false);
            setImageSrc(null);
          }}
        />
      )}
    </div>
  );
}
